import { useSyncExternalStore } from "react";

/** The inputs that bring up a keyboard; a date, a checkbox or a button opens a picker or nothing. */
const KEYBOARD_INPUTS = new Set(["text", "email", "password", "search", "tel", "url", "number"]);

/** How long the bars wait once a box lets go, so moving straight to the next box (Safari's ^ v) does not flicker them. */
export const LET_GO_MS = 150;

/** Whether this element, focused, brings up a keyboard. */
export function bringsUpKeyboard(element: Element | null): boolean {
  if (!(element instanceof HTMLElement) || element.inputMode === "none") return false;
  if (element instanceof HTMLTextAreaElement) return !element.readOnly && !element.disabled;
  if (element instanceof HTMLInputElement) return KEYBOARD_INPUTS.has(element.type) && !element.readOnly && !element.disabled;
  return element.isContentEditable === true;
}

/** A window in the background — another app, another tab — is typed in by nobody, whatever it last focused. */
function typingNow(): boolean {
  return (
    !!window.matchMedia?.("(pointer: coarse)").matches && document.hasFocus() && bringsUpKeyboard(document.activeElement)
  );
}

/**
 * The keyboard going away is asked about too: a box disabled or taken off the
 * page while focused — the teammate box once the team is full, a form sent
 * with Enter — loses the focus with no focusout, and the bars would stay away.
 */
function onViewportResize() {
  set(typingNow());
}

let typing = false;
let letGo: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function set(next: boolean) {
  if (next === typing) return;
  typing = next;
  listeners.forEach((listener) => listener());
}

/** Letting go waits, whether the focus left for nothing or for a picker, which rises where the keyboard was. */
function letGoSoon() {
  clearTimeout(letGo);
  letGo = setTimeout(() => set(typingNow()), LET_GO_MS);
}

function onFocusIn() {
  if (typingNow()) {
    clearTimeout(letGo);
    set(true);
  } else {
    letGoSoon();
  }
}

function subscribe(onChange: () => void) {
  if (listeners.size === 0) {
    typing = typingNow();
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", letGoSoon);
    window.visualViewport?.addEventListener("resize", onViewportResize);
  }
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0) {
      // Nobody follows it now: the next bar drawn starts from nothing typed, not from this.
      typing = false;
      clearTimeout(letGo);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", letGoSoon);
      window.visualViewport?.removeEventListener("resize", onViewportResize);
    }
  };
}

/**
 * Whether somebody is typing on a touch screen: a box that brings up a
 * keyboard has the focus. Safari keeps a bar fixed to the bottom where the
 * bottom of the screen was, behind and above the keyboard, so the bars there
 * are not drawn while this holds (ADR 0071). Told from the focus rather than
 * the keyboard's size, which Safari gives only once the keyboard has risen.
 */
export function useTyping(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => typing,
    () => false,
  );
}
